/** Inline SVG icons for the sidebar nav items, keyed by their i18n nav key.
 *
 * No icon library is bundled (keeps the SPA lean), so these are hand-drawn
 * 24×24 outline glyphs rendered with `currentColor` so they inherit the
 * link's active / hover colour. A nav key with no entry here simply renders
 * no icon (graceful) — the label still shows.
 */
import type { ReactNode } from "react";

const ICONS: Record<string, ReactNode> = {
  // ---- main app ----
  "nav.dashboard": (
    <>
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </>
  ),
  "nav.campaigns": (
    <>
      <path d="M5 21V4" />
      <path d="M5 4h12l-2.5 4L17 12H5" />
    </>
  ),
  "nav.analytics": (
    <>
      <path d="M4 20V10" />
      <path d="M10 20V4" />
      <path d="M16 20v-6" />
      <path d="M3 20h18" />
    </>
  ),
  "nav.contacts": (
    <>
      <rect x="5" y="3" width="14" height="18" rx="2" />
      <circle cx="12" cy="10" r="2.3" />
      <path d="M8.5 16.5c.6-1.8 2-2.6 3.5-2.6s2.9.8 3.5 2.6" />
      <path d="M3 7.5h2M3 12h2M3 16.5h2" />
    </>
  ),
  "nav.calendar": (
    <>
      <rect x="3.5" y="5" width="17" height="15.5" rx="2" />
      <path d="M3.5 9.5h17" />
      <path d="M8 3v4M16 3v4" />
    </>
  ),
  "nav.sessions": (
    <>
      <rect x="3" y="6" width="12" height="12" rx="2" />
      <path d="M15 10l6-3v10l-6-3z" />
    </>
  ),
  "nav.support": (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="3.4" />
      <path d="M6.2 6.2l3.4 3.4M14.4 14.4l3.4 3.4M17.8 6.2l-3.4 3.4M9.6 14.4l-3.4 3.4" />
    </>
  ),
  // ---- grouped admin ----
  "nav.orgSettings": (
    <>
      <path d="M4 21V6l8-3 8 3v15" />
      <path d="M9 21v-5h6v5" />
      <path d="M8 8h2M14 8h2M8 12h2M14 12h2" />
    </>
  ),
  "nav.members": (
    <>
      <circle cx="9" cy="8.5" r="3" />
      <path d="M3.5 19c0-3.2 2.5-5 5.5-5s5.5 1.8 5.5 5" />
      <path d="M16 6.2a3 3 0 010 5.6" />
      <path d="M17.5 14.2c2 .5 3.5 2.1 3.5 4.8" />
    </>
  ),
  "nav.roles": (
    <>
      <path d="M12 3l7 3v5c0 4.5-3 7.6-7 9-4-1.4-7-4.5-7-9V6z" />
      <path d="M9 12l2 2 3.5-3.8" />
    </>
  ),
  "nav.sso": (
    <>
      <path d="M13 3h5a1 1 0 011 1v16a1 1 0 01-1 1h-5" />
      <path d="M3.5 12h10" />
      <path d="M10 8l4 4-4 4" />
    </>
  ),
  "nav.apiKeys": (
    <>
      <circle cx="8" cy="12" r="3.4" />
      <path d="M11.4 12H21M18 12v3M15 12v2.4" />
    </>
  ),
  "nav.webhooks": (
    <>
      <path d="M10 13.5a4 4 0 005.7 0l2.1-2.1a4 4 0 00-5.7-5.7L11 6.7" />
      <path d="M14 10.5a4 4 0 00-5.7 0L6.2 12.6a4 4 0 005.7 5.7L13 17.3" />
    </>
  ),
  "nav.domains": (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M3.5 12h17" />
      <path d="M12 3.5c2.4 2.5 2.4 14.5 0 17M12 3.5c-2.4 2.5-2.4 14.5 0 17" />
    </>
  ),
  "nav.billing": (
    <>
      <rect x="3" y="5.5" width="18" height="13" rx="2" />
      <path d="M3 10h18M7 14.5h4" />
    </>
  ),
  "nav.retention": (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 2" />
    </>
  ),
  "nav.branding": (
    <>
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <circle cx="9" cy="9.5" r="1.8" />
      <path d="M5 18l4.5-4.5 3 3L16 12l4 4.5" />
    </>
  ),
  "nav.kyb": (
    <>
      <rect x="6" y="4" width="12" height="17" rx="2" />
      <path d="M9.5 4h5v2h-5z" />
      <path d="M9 13l2 2 4-4" />
    </>
  ),
  "nav.customFields": (
    <>
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <path d="M8 9h8M8 12.5h8M8 16h5" />
    </>
  ),
  "nav.audit": (
    <>
      <path d="M6 3h8l4 4v14H6z" />
      <path d="M14 3v4h4" />
      <path d="M9 13.5l2 2 3.5-3.5" />
    </>
  ),
  // ---- partner console ----
  "nav.partner.overview": (
    <>
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </>
  ),
  "nav.partner.organizations": (
    <>
      <path d="M4 21V6l8-3 8 3v15" />
      <path d="M9 21v-5h6v5" />
      <path d="M8 8h2M14 8h2M8 12h2M14 12h2" />
    </>
  ),
  "nav.partner.team": (
    <>
      <circle cx="9" cy="8.5" r="3" />
      <path d="M3.5 19c0-3.2 2.5-5 5.5-5s5.5 1.8 5.5 5" />
      <path d="M16 6.2a3 3 0 010 5.6" />
      <path d="M17.5 14.2c2 .5 3.5 2.1 3.5 4.8" />
    </>
  ),
  "nav.partner.commercials": (
    <>
      <rect x="3.5" y="7.5" width="17" height="11.5" rx="2" />
      <path d="M9 7.5V6a2 2 0 012-2h2a2 2 0 012 2v1.5" />
      <path d="M3.5 12h17" />
    </>
  ),
  "nav.partner.commissions": (
    <>
      <path d="M6.5 17.5l11-11" />
      <circle cx="8" cy="8" r="2" />
      <circle cx="16" cy="16" r="2" />
    </>
  ),
  "nav.partner.payouts": (
    <>
      <rect x="3" y="6" width="18" height="12" rx="2" />
      <path d="M15.5 11.5h3.5v2.5h-3.5a1.25 1.25 0 010-2.5z" />
    </>
  ),
  "nav.partner.settings": (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2.5v3M12 18.5v3M2.5 12h3M18.5 12h3M5 5l2 2M17 17l2 2M19 5l-2 2M7 17l-2 2" />
    </>
  ),
};

/** Renders the icon glyph for a nav key, or null when none is mapped. */
export function NavIcon({ name }: { name: string }): ReactNode {
  const glyph = ICONS[name];
  if (!glyph) return null;
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="shrink-0"
    >
      {glyph}
    </svg>
  );
}
